import { HyperFormula, DetailedCellError } from 'hyperformula';
import { getExpandableRowInsertPlan } from '../expandSheetForCustomTables';
import { coerceSheetValue, formatFormulaDisplay } from './coerceSheetValue';
import { rewriteUnsupportedIndirectFormulas } from './rewriteUnsupportedFormulas';

const HF_OPTIONS = {
  licenseKey: 'gpl-v3',
  useColumnIndex: true,
  // Prefer blanks over propagating noisy errors into dependent cells when possible.
  evaluateNullToZero: false
};

const looksLikeToken = value => {
  const text = String(value ?? '');
  return text.includes('{{') || text.includes('[[');
};

const asSheetList = templateSheets => {
  if (Array.isArray(templateSheets)) {
    return templateSheets.filter(sheet => sheet?.name);
  }
  return templateSheets?.name ? [templateSheets] : [];
};

/**
 * Build a 2D sheet content array for HyperFormula from a template layout sheet.
 * Formula cells keep their formula; {{tokens}} start blank (synced later).
 */
export const buildHfContentFromTemplate = sheet => {
  const rows = Math.max(sheet?.rows || 0, sheet?.values?.length || 0, sheet?.formulas?.length || 0);
  const cols = Math.max(
    sheet?.cols || 0,
    ...(sheet?.values || []).map(r => (Array.isArray(r) ? r.length : 0)),
    ...(sheet?.formulas || []).map(r => (Array.isArray(r) ? r.length : 0)),
    1
  );

  const content = [];
  for (let r = 0; r < rows; r++) {
    const row = [];
    for (let c = 0; c < cols; c++) {
      const formula = sheet?.formulas?.[r]?.[c];
      if (formula && String(formula).trim().startsWith('=')) {
        row.push(String(formula).trim());
        continue;
      }

      const raw = sheet?.values?.[r]?.[c];
      if (looksLikeToken(raw)) {
        row.push(null);
        continue;
      }

      row.push(coerceSheetValue(raw));
    }
    content.push(row);
  }
  return content;
};

const applySheetGeometry = (hf, sheetName, documentState) => {
  const sheetId = hf.getSheetId(sheetName);
  if (sheetId == null) return null;

  // Match Apps Script insertRowsAfter(startRow, extra): HF addRows before index startRow (0-based).
  const plan = getExpandableRowInsertPlan(sheetName, documentState);
  plan.forEach(({ startRow, extra }) => {
    if (extra > 0) {
      hf.addRows(sheetId, [startRow, extra]);
    }
  });

  // HF 3 has no INDIRECT — rewrite template patterns (e.g. 21st funds TOTAL) after expands.
  rewriteUnsupportedIndirectFormulas(hf, sheetId);
  return sheetId;
};

/**
 * Create a HyperFormula workbook for every template sheet so formulas can
 * reference cells across sheets (e.g. =Sheet2!A1 / ='Other Sheet'!B5).
 * Expandable row inserts stay aligned with the visual expander per sheet.
 */
export const createSheetFormulaEngine = (templateSheets, documentState, activeSheetName) => {
  const sheets = asSheetList(templateSheets);
  const sheetsContent = {};

  sheets.forEach(sheet => {
    sheetsContent[sheet.name] = buildHfContentFromTemplate(sheet);
  });

  // Empty workbook fallback keeps callers safe.
  if (!Object.keys(sheetsContent).length) {
    sheetsContent.Sheet1 = [[null]];
  }

  const hf = HyperFormula.buildFromSheets(sheetsContent, HF_OPTIONS);

  const sheetIdsByName = {};
  sheets.forEach(sheet => {
    const sheetId = applySheetGeometry(hf, sheet.name, documentState);
    if (sheetId != null) {
      sheetIdsByName[sheet.name] = sheetId;
    }
  });

  let activeName = activeSheetName && sheetIdsByName[activeSheetName] != null
    ? activeSheetName
    : sheets[0]?.name || Object.keys(sheetIdsByName)[0];

  const resolveSheetId = sheetName => {
    const name = sheetName || activeName;
    if (name != null && sheetIdsByName[name] != null) return sheetIdsByName[name];
    return sheetIdsByName[activeName] ?? null;
  };

  const address = (row0, col0, sheetName) => {
    const sheetId = resolveSheetId(sheetName);
    if (sheetId == null) return null;
    return { sheet: sheetId, col: col0, row: row0 };
  };

  return {
    hf,
    get sheetId() {
      return resolveSheetId(activeName);
    },
    get sheetName() {
      return activeName;
    },
    sheetIdsByName,
    setActiveSheet(sheetName) {
      if (sheetName && sheetIdsByName[sheetName] != null) {
        activeName = sheetName;
      }
    },
    destroy() {
      try {
        hf.destroy();
      } catch (e) {
        // ignore
      }
    },
    setCellValue(row0, col0, value, sheetName) {
      const cell = address(row0, col0, sheetName);
      if (!cell) return;
      try {
        hf.setCellContents(cell, [[coerceSheetValue(value)]]);
      } catch (e) {
        // ignore invalid addresses after edge-case expands
      }
    },
    hasFormula(row0, col0, sheetName) {
      const cell = address(row0, col0, sheetName);
      if (!cell) return false;
      try {
        return Boolean(hf.getCellFormula(cell));
      } catch (e) {
        return false;
      }
    },
    getFormulaText(row0, col0, sheetName) {
      const cell = address(row0, col0, sheetName);
      if (!cell) return '';
      try {
        return hf.getCellFormula(cell) || '';
      } catch (e) {
        return '';
      }
    },
    getFormulaDisplay(row0, col0, sheetName) {
      const cell = address(row0, col0, sheetName);
      if (!cell) return '—';
      try {
        const value = hf.getCellValue(cell);
        if (value instanceof DetailedCellError) return '—';
        return formatFormulaDisplay(value);
      } catch (e) {
        return '—';
      }
    }
  };
};
